import { useMemo, useRef } from 'react';
import type {
  ColDef,
  GridApi,
  RowSelectionOptions,
  GetRowIdParams,
  SizeColumnsToFitGridStrategy,
  SizeColumnsToFitProvidedWidthStrategy,
  SizeColumnsToContentStrategy,
  ValueFormatterFunc,
  ValueFormatterParams,
} from 'ag-grid-enterprise';
import type { IRow } from '../types/TableRows';
import { formatToTwoDecimals } from '../utils/format-number';

export const useGridConfig = () => {
  const gridApi = useRef<GridApi | null>(null);

  const typeFormatter = (params: ValueFormatterParams<IRow, string>): string => {
    const typeMap: Record<string, string> = {
      MATCHED: 'M',
      UNMATCHED: 'U',
    };

    const value = params.value?.toUpperCase();

    return typeMap[value!];
  };

  const twoDecimalFormatter: ValueFormatterFunc<IRow, string> = (params) => {
    const { value } = params as { value: string | number | null | undefined };
    const formattedValue: string = formatToTwoDecimals(value);

    return formattedValue;
  };

  const colDefs: ColDef<IRow>[] = useMemo(
    () => [
      {
        field: 'type',
        headerName: 'UM',
        filter: false,
        maxWidth: 77,
        valueFormatter: typeFormatter,
      },
      { field: 'breakId', filter: 'agTextColumnFilter', hide: true },
      { field: 'accountName', filter: 'agTextColumnFilter' },
      { field: 'clientName', filter: 'agTextColumnFilter' },
      { field: 'breakAge', maxWidth: 150 },
      { field: 'securityTypeManager', headerName: 'Security Type' },
      { field: 'securityDescriptionManager', headerName: 'Security Desc Mgt' },
      { field: 'securityDescriptionBank', headerName: 'Security Desc Bank' },
      {
        field: 'marketValueLocalVariance',
        headerName: 'MV Variance',
        cellStyle: { textAlign: 'right' },
        maxWidth: 170,
        valueFormatter: twoDecimalFormatter,
      },
      {
        field: 'mktValueLocalPctVariance',
        headerName: 'MV Variance (%)',
        cellStyle: { textAlign: 'right' },
        maxWidth: 150,
        valueFormatter: twoDecimalFormatter,
      },
      { field: 'nextReviewDate', maxWidth: 150 },
      { field: 'commentDate', maxWidth: 140 },
      { field: 'comment' },
    ],
    []
  );

  const defaultColDef = useMemo<ColDef>(
    () => ({
      initialWidth: 200,
      sortable: true,
      resizable: true,
      filter: true,
      wrapHeaderText: true,
      autoHeaderHeight: true,
      headerStyle: { 'font-size': '14px' },
    }),
    []
  );

  const rowSelection = useMemo<RowSelectionOptions>(
    () => ({
      mode: 'singleRow',
      checkboxes: false,
      enableClickSelection: true,
    }),
    []
  );

  const autoSizeStrategy = useMemo<
    SizeColumnsToFitGridStrategy | SizeColumnsToFitProvidedWidthStrategy | SizeColumnsToContentStrategy
  >(() => {
    return {
      type: 'fitGridWidth',
      defaultMinWidth: 120,
    };
  }, []);

  const getRowId = (params: GetRowIdParams) => String(params.data.breakId);

  return {
    gridApi,
    colDefs,
    defaultColDef,
    rowSelection,
    getRowId,
    autoSizeStrategy,
  };
};
